<template>
  <section v-if="items.length">
    <div class="row justify-center items-center q-gutter-md q-pb-md heading">
      <div class="title">
        <h2 class="text-h4 text-weight-medium no-margin">
          Shop By Category
        </h2>
      </div>
    </div>

    <div class="row justify-center items-stretch q-col-gutter-md">
      <div
        v-for="item in items"
        :key="item.id"
        class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2"
      >
        <q-item
          :to="`/category/${item.slug}`"
          class="bg-blue-grey-9 rounded-borders"
        >
          <q-item-section style="height: 75px">
            <h3 class="text-h6 no-margin ellipsis-2-lines">
              {{ item.name }}
            </h3>
          </q-item-section>
          <q-item-section
            v-if="item.imageUrl"
            side
          >
            <q-img
              :src="item.imageUrl"
              width="75px"
              fit="cover"
              ratio="1"
              spinner-size="sm"
              spinner-color="primary"
            />
          </q-item-section>
        </q-item>
      </div>

      <div
        v-if="items.length < total"
        class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-2"
      >
        <q-item
          :disable="status == 'pending'"
          class="bg-grey-9 rounded-borders"
          clickable
          @click="perPage += 10"
        >
          <q-item-section style="height: 75px">
            <h3 class="text-h6 no-margin ellipsis-2-lines">
              Show more
            </h3>
          </q-item-section>
          <q-item-section side>
            <q-icon
              :name="mdiChevronRight"
              size="lg"
            />
          </q-item-section>
        </q-item>
      </div>
    </div>
  </section>
</template>

<script lang="ts" setup>
import { mdiChevronRight } from '@quasar/extras/mdi-v7';
import type { Category } from '~/types/search';
import { NullOrder, Order } from '~/types/typesense';

const props = defineProps({
  category: {
    type: Object as PropType<Category>,
    required: true,
  },
});

const { $typesense } = useNuxtApp();

const { buildFieldsList, buildFilterBy, buildSortBy } = useTypesenseUtils();

const perPage = ref(10);

const { data, status } = useAsyncData(`categories_${props.category.id}_children`,
  () => $typesense.search<Category>('kygunco_categories', {
    q: '*',
    query_by: '',
    filter_by: buildFilterBy<Category>({
      parentId: { $eq: props.category.id.toString() },
    }),
    include_fields: buildFieldsList<Category>([
      'id',
      'name',
      'slug',
      'imageUrl',
    ]),
    sort_by: buildSortBy<Category>({
      order: { $order: Order.Asc, $null: NullOrder.Last },
      name: { $order: Order.Asc },
    }),
    highlight_fields: 'none',
    per_page: perPage.value,
  }),
  {
    lazy: true,
    watch: [perPage],
    getCachedData: getCachedPayload,
  },
);

const items = computed(() => data.value?.hits?.map(h => h.document) ?? []);

const total = computed(() => data.value?.found ?? 0);
</script>
